// 原控制组件零修改：桥接层只读最新设备信息，手动操作仍使用原组件/原接口。
import { createAiClient, identity } from '@/ai/services/api'

let surfaceOwner = null
export function claimManualSurface(owner) { if(surfaceOwner && surfaceOwner !== owner)return false; surfaceOwner=owner;return true }
export function releaseManualSurface(owner) { if(surfaceOwner===owner)surfaceOwner=null }

export function detachedDevice(device) { return JSON.parse(JSON.stringify(device)) }

// Preview admission and fetched responses use the same target identity boundary.
export function matchesManualDevice(device, ref) {
  return !!device && !!ref && !!ref.productId && !!ref.regionId &&
    device.productId===ref.productId && device.regionId===ref.regionId &&
    (!ref.deviceId || device.deviceId===ref.deviceId)
}

// 原弹窗只在手动接口成功后更新objData；同步其确认记录，不监听乐观的userControlData。
// 保留AI卡片原产品类型，禁止跨设备/区域串写。
export function confirmedManualPreview(current, update, ref = current) {
  if(!current || !matchesManualDevice(update,ref))return null
  const next={...detachedDevice(current),...detachedDevice(update),productCode:current.productCode}
  return JSON.stringify(next)===JSON.stringify(current)?null:next
}

// Keep the object held by the original control and its in-flight response.
// Vue.set also makes newly returned state fields reactive.
export function syncManualDevice(target, snapshot, set) {
  for (const key of Object.keys(snapshot)) set(target, key, snapshot[key])
  return target
}

// 仅合并正在进行的读取，不缓存已完成状态。最多4个详情请求，避免长对话同时加载压垮接口。
const pendingReads = new Map()
const readQueue = []
const MAX_READS = 4
let activeReads = 0

function forgetRead(job) {
  if (pendingReads.get(job.key) === job) pendingReads.delete(job.key)
}
function pumpReads() {
  while (activeReads < MAX_READS && readQueue.length) {
    const job = readQueue.shift()
    if (!job.users.size || job.scope !== identity()) {
      forgetRead(job)
      job.reject(Error('ACCOUNT_CHANGED'))
      continue
    }
    activeReads++
    const finish = () => {
      activeReads--
      forgetRead(job)
      if (job.client) job.client.close()
      pumpReads()
    }
    try {
      job.client = createAiClient()
      Promise.resolve(job.client.post('device/get', { ref: job.ref })).then(
        result => { job.resolve(result); finish() },
        error => { job.reject(error); finish() }
      )
    } catch (error) { job.reject(error); finish() }
  }
}

export function createManualDeviceReader() {
  const scope = identity(), leases = new Set()
  let closed = false
  return {
    async get(ref, { fresh = false } = {}) {
      if (closed || scope !== identity()) throw Error('ACCOUNT_CHANGED')
      const snapshot = detachedDevice(ref)
      const key = JSON.stringify([scope, snapshot.regionId, snapshot.productId, snapshot.deviceId || null, snapshot.deviceId ? null : snapshot.title])
      // 控制后的读取必须新建，不能复用控制前尚未返回的请求。
      let job = fresh ? null : pendingReads.get(key)
      if (!job) {
        job = { key, scope, ref: snapshot, users: new Set(), client: null }
        job.promise = new Promise((resolve, reject) => { job.resolve = resolve; job.reject = reject })
        pendingReads.set(key, job)
        readQueue.push(job)
      }
      const lease = { job }
      leases.add(lease); job.users.add(lease)
      pumpReads()
      try {
        const result = await job.promise
        if (closed || scope !== identity()) throw Error('ACCOUNT_CHANGED')
        if (!result || !result.productCode || !matchesManualDevice(result,snapshot)) throw Error('DEVICE_QUERY_FAILED')
        return detachedDevice(result)
      } finally { leases.delete(lease); job.users.delete(lease) }
    },
    valid: () => !closed && scope === identity(),
    close() {
      closed = true
      for (const lease of leases) {
        lease.job.users.delete(lease)
        // 一个卡片离开，不能取消仍被其他卡片使用的共享读取。
        if (!lease.job.users.size) {
          forgetRead(lease.job)
          if (lease.job.client) lease.job.client.close()
        }
      }
      leases.clear()
      pumpReads()
    }
  }
}
